import { supabase } from "@/integrations/supabase/client";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { generatedFilePath } from '@/components/workflows/docProvenance';

/**
 * Generate a PDF from provided HTML string, upload to storage, and return file details.
 * 
 * KNOWN LIMITATION: Signature blocks may occasionally split across pages due to
 * devicePixelRatio variability (browser zoom, OS scaling, display settings).
 * This does not affect signature validity, only aesthetic presentation.
 * 
 * FUTURE ENHANCEMENT: Migrate to Puppeteer-based server-side PDF generation
 * for proper CSS page-break support and consistent cross-device rendering.
 * 
 * @param html - The HTML content to convert to PDF
 * @param documentId - The document ID for storage path
 * @param documentName - Optional document name for the file
 * @returns Promise with file URL, path, and size
 */
export async function generatePdfAndUpload(
  html: string,
  documentId: string,
  documentName?: string
): Promise<{ file_url: string; file_path: string; file_size: number }> {
  
  console.log('🔵 [PDF] Generating PDF using browser rendering (same as preview)');

  // Create the EXACT same HTML document as generatePdfPreview()
  const fullHtmlDocument = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Document</title>
      <style>
        /* Ecclesiastical Document Styling for PDFs */
        * {
          font-family: Cambria, Georgia, 'Times New Roman', serif !important;
        }
        
        @page {
          size: Letter;
          margin: 0.75in;
        }
        
        body {
          margin: 0;
          padding: 0.75in;
          font-family: Cambria, Georgia, 'Times New Roman', serif;
          font-size: 11pt;
          line-height: 1.85;
          color: #1a1a1a;
          background: white;
          box-sizing: border-box;
          min-height: 11in;
        }
        
        h1 { 
          font-size: 18pt; 
          font-weight: bold; 
          margin: 0 0 20pt 0;
          text-align: center;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          border-bottom: 2px solid #c9a961;
          padding-bottom: 14pt;
        }
        
        h2 { 
          font-size: 13pt; 
          font-weight: bold; 
          margin: 24pt 0 10pt 0;
          border-bottom: 1px solid #bfbfbf;
          padding-bottom: 6pt;
          color: #262626;
        }
        
        h3 { 
          font-size: 12pt; 
          font-weight: bold; 
          font-style: italic;
          margin: 18pt 0 8pt 0;
          color: #333;
        }
        
        p { 
          margin: 0 0 14pt 0;
          font-size: 11pt;
          text-align: justify;
        }
        
        /* Intro paragraphs - italicized with gold accent */
        .intro, p.intro, p:first-of-type {
          font-style: italic;
          color: #4d4d4d;
          border-left: 3px solid #c9a961;
          padding-left: 16pt;
          margin-bottom: 20pt;
        }
        
        /* Scripture references - smaller, italic */
        .reference, em {
          font-style: italic;
          color: #666;
        }
        
        /* Conclusion statements - centered, bold */
        .conclusion {
          text-align: center;
          font-weight: bold;
          font-size: 12pt;
          margin-top: 28pt;
        }
        
        li { 
          margin: 8pt 0;
          font-size: 11pt;
          line-height: 1.7;
        }
        
        ul, ol {
          margin: 14pt 0;
          padding-left: 28pt;
        }
        
        strong, b { font-weight: bold; }
        
        hr {
          border: none;
          border-top: 1px solid #ccc;
          margin: 24pt 0;
        }
        
        /* Signature block styles */
        .signature-block {
          margin-top: 40pt;
          margin-bottom: 24pt;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        
        .signature-section {
          margin-top: 40pt;
          padding-top: 24pt;
          border-top: 1px solid #ccc;
        }
        
        .signature-line {
          border-bottom: 1px solid #000;
          width: 280pt;
          margin-bottom: 6pt;
          height: 1px;
        }
      </style>
    </head>
    <body>
      ${html}
    </body>
    </html>
  `;

  // DIAGNOSTIC: Check what HTML is being passed to iframe
  console.log('🔵 [FINALIZE DIAGNOSTIC] HTML passed to generatePdfAndUpload (first 500):');
  console.log(html.substring(0, 500));
  console.log('🔵 [FINALIZE DIAGNOSTIC] HTML passed to generatePdfAndUpload (LAST 500):');
  console.log(html.substring(Math.max(0, html.length - 500)));
  console.log('🔵 [FINALIZE DIAGNOSTIC] Does HTML contain "signature-block"?', 
    html.includes('signature-block'));
  console.log('🔵 [FINALIZE DIAGNOSTIC] Does HTML contain "signature-line"?',
    html.includes('signature-line'));
  console.log('🔵 [FINALIZE DIAGNOSTIC] Does HTML contain "Trenton Frazer"?',
    html.includes('Trenton Frazer'));
  console.log('🔵 [FINALIZE DIAGNOSTIC] HTML total length:', html.length);

  console.log('🔵 [FINALIZE DIAGNOSTIC] Full HTML document (LAST 800 chars):');
  console.log(fullHtmlDocument.substring(Math.max(0, fullHtmlDocument.length - 800)));

  // Create VISIBLE iframe to render the document
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.left = '0';
  iframe.style.top = '0';
  iframe.style.zIndex = '10000';
  iframe.style.width = '816px'; // 8.5 inches at 96 DPI
  iframe.style.height = '1056px'; // 11 inches at 96 DPI
  iframe.style.border = 'none';
  iframe.style.backgroundColor = 'white';
  
  document.body.appendChild(iframe);

  try {
    // Write the HTML into the iframe
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) throw new Error('Cannot access iframe document');
    
    iframeDoc.open();
    iframeDoc.write(fullHtmlDocument);
    iframeDoc.close();

    // DIAGNOSTIC: Check what actually got written to iframe
    console.log('🔵 [IFRAME DIAGNOSTIC] Iframe body innerHTML (LAST 500 chars):');
    console.log(iframeDoc.body.innerHTML.substring(Math.max(0, iframeDoc.body.innerHTML.length - 500)));
    console.log('🔵 [IFRAME DIAGNOSTIC] Does iframe contain "signature-block"?',
      iframeDoc.body.innerHTML.includes('signature-block'));
    console.log('🔵 [IFRAME DIAGNOSTIC] Does iframe contain "signature-line"?',
      iframeDoc.body.innerHTML.includes('signature-line'));
    console.log('🔵 [IFRAME DIAGNOSTIC] Does iframe contain "Trenton Frazer"?',
      iframeDoc.body.innerHTML.includes('Trenton Frazer'));

    // Additional requested diagnostics
    console.log('🔵 [IFRAME HEIGHT] iframe.scrollHeight:', (iframe as any).scrollHeight);
    console.log('🔵 [IFRAME HEIGHT] iframe.offsetHeight:', iframe.offsetHeight);
    console.log('🔵 [SIGNATURE CHECK] Last 200 chars of iframe body:',
      iframeDoc.body.innerHTML.slice(-200));

    // Wait for fonts to load in iframe
    await iframeDoc.fonts.ready;
    
    // Additional delay to ensure full rendering (including Cambria font)
    await new Promise(resolve => setTimeout(resolve, 500));

    // PHASE 3: Real DOM Measurement for Page Breaks
    console.log('📏 [PAGE BREAK] Waiting for layout to settle...');
    await new Promise(resolve => setTimeout(resolve, 150));

    const signatureBlocks = iframeDoc.querySelectorAll('.signature-block');
    
    if (signatureBlocks.length > 0) {
      console.log('📏 [PAGE BREAK] Found signature blocks:', signatureBlocks.length);
      
      // Treat first signature block as anchor point (both blocks stay together)
      const firstBlock = signatureBlocks[0] as HTMLElement;
      const signatureTop = firstBlock.offsetTop;
      const signatureHeight = 300; // Estimated height of both signature blocks together
      
      const PAGE_HEIGHT = 1056; // 11 inches at 96 DPI
      const positionInPage = signatureTop % PAGE_HEIGHT;
      const remainingSpace = PAGE_HEIGHT - positionInPage;
      
      console.log('📏 [PAGE BREAK] First signature block:');
      console.log('  - Absolute position:', signatureTop, 'px from document top');
      console.log('  - Position in current page:', positionInPage, 'px');
      console.log('  - Remaining space on page:', remainingSpace, 'px');
      console.log('  - Required space:', signatureHeight, 'px');
      
      // If signatures won't fit on current page, push to next page
      if (remainingSpace < signatureHeight && remainingSpace > 100) {
        const spacerHeight = remainingSpace + 72; // Fill remaining space + top margin of next page
        
        console.log('✅ [PAGE BREAK] Injecting spacer:', spacerHeight, 'px');
        
        const spacer = iframeDoc.createElement('div');
        spacer.style.height = `${spacerHeight}px`;
        spacer.style.width = '100%';
        spacer.style.backgroundColor = 'transparent';
        spacer.style.margin = '0';
        spacer.style.padding = '0';
        spacer.setAttribute('data-page-break-spacer', 'signature-block');
        
        firstBlock.parentNode?.insertBefore(spacer, firstBlock);
        
        // Let DOM update
        await new Promise(resolve => setTimeout(resolve, 100));
        console.log('✅ [PAGE BREAK] Spacer injected, signatures pushed to next page');
      } else if (remainingSpace >= signatureHeight) {
        console.log('✅ [PAGE BREAK] Signature blocks fit on current page - no spacer needed');
      } else {
        console.log('⚠️ [PAGE BREAK] Edge case: signatures near page boundary but close to top');
      }
    } else {
      console.log('📏 [PAGE BREAK] No signature blocks found in document');
    }

    // Capture signature field coordinates
    const signatureFields: any[] = [];
    const signatureFieldElements = iframeDoc.querySelectorAll('.signature-field');

    // Diagnostic: Check iframe DPI context
    console.log('🔴 [IFRAME CONFIG]', {
      iframeWidth: iframe.style.width,
      windowInnerWidth: iframe.contentWindow?.innerWidth,
      devicePixelRatio: iframe.contentWindow?.devicePixelRatio || window.devicePixelRatio,
      documentDPI: iframe.contentDocument?.defaultView?.devicePixelRatio
    });

    // Diagnostic: Check CSS unit conversion
    const testDiv = iframeDoc.querySelector('.signature-field') as HTMLElement;
    if (testDiv) {
      console.log('🔴 [CSS TEST]', {
        cssWidth: testDiv.style.width,
        computedWidth: iframe.contentWindow?.getComputedStyle(testDiv)?.width,
        rectWidth: testDiv.getBoundingClientRect().width
      });
    }

    console.log('🔵 [SIGNATURE FIELDS] Found', signatureFieldElements.length, 'signature field elements');

    signatureFieldElements.forEach((element: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      const fieldId = element.getAttribute('data-signature-id');
      const signerRole = element.getAttribute('data-signer-role');
      
      console.log('🔴 [PRE-SCALE] Raw rect:', {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      });
      
      // Calculate which page (assuming 11 inch pages)
      const pageHeight = 11 * 96; // 11 inches at 96 DPI
      const pageNumber = Math.floor(rect.top / pageHeight) + 1;
      
      // Convert DOM px to PDF points correctly, accounting for html2canvas scale and PDF width
      // html2canvas uses scale=2 below and we render at a fixed windowWidth of 816px -> canvas.width = 1632px
      // The PDF page width is 8.5in = 612pt, so pt/px = 612 / (816 * 2) = 0.375
      const WINDOW_WIDTH_PX = 816;
      const HTML2CANVAS_SCALE = 2;
      const PDF_WIDTH_PT = 8.5 * 72; // 612
      const pxToPt = PDF_WIDTH_PT / (WINDOW_WIDTH_PX * HTML2CANVAS_SCALE);
      console.log('🔴 [SCALE] pxToPt:', pxToPt);

      const scaledValues = {
        x: Math.round(rect.left * pxToPt),
        y: Math.round(rect.top * pxToPt),
        width: Math.round(rect.width * pxToPt),
        height: Math.round(rect.height * pxToPt)
      };
      
      console.log('🔴 [POST-SCALE] Scaled values:', scaledValues);
      
      signatureFields.push({
        fieldId: fieldId,
        signerRole: signerRole,
        pageNumber: pageNumber,
        ...scaledValues
      });
    });

    console.log('🔵 [SIGNATURE FIELDS] Captured metadata:', JSON.stringify(signatureFields));

    // Compute full content height and adjust iframe size to fit all content
    const docEl = iframeDoc.documentElement;
    let contentHeight = Math.max(docEl.scrollHeight, iframeDoc.body.scrollHeight, iframeDoc.body.offsetHeight);

    // Check for signature block which might extend beyond body height due to large margins
    const sigEl = iframeDoc.querySelector('.signature-block') as HTMLElement | null;
    if (sigEl) {
      const rect = sigEl.getBoundingClientRect();
      const top = rect.top + (iframe.contentWindow?.scrollY || 0);
      const bottom = rect.bottom + (iframe.contentWindow?.scrollY || 0);
      
      console.log('🔵 [CONTENT HEIGHT] documentElement.scrollHeight:', docEl.scrollHeight);
      console.log('🔵 [CONTENT HEIGHT] body.scrollHeight:', iframeDoc.body.scrollHeight);
      console.log('🔵 [CONTENT HEIGHT] body.offsetHeight:', iframeDoc.body.offsetHeight);
      console.log('🔵 [CONTENT HEIGHT] signature-block top:', top, 'bottom:', bottom);
      
      // Use signature block bottom if it extends beyond calculated height
      if (bottom > contentHeight) {
        console.log(`🟢 [FIX] Extending contentHeight from ${contentHeight}px to ${bottom}px to include signature block`);
        contentHeight = bottom;
      }
    } else {
      console.log('🟡 [CONTENT HEIGHT] signature-block element not found in DOM');
    }

    // Add 50px safety margin to ensure clean capture
    contentHeight = contentHeight + 50;

    iframe.style.height = contentHeight + 'px';

    // Small delay after resizing iframe to let layout settle
    await new Promise(resolve => setTimeout(resolve, 100));

    console.log('🔵 [PDF] Content rendered in iframe, capturing...');

    // Capture the rendered content with html2canvas (full height)
    const canvas = await html2canvas(iframeDoc.body, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: 816,
      windowHeight: contentHeight,
      width: 816,
      height: contentHeight,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
    });

    console.log('🔵 [PDF] Canvas captured:', canvas.width, 'x', canvas.height);

    // Convert canvas to image
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    
    // Create PDF (Letter size = 8.5" x 11")
    const pdf = new jsPDF({
      unit: 'in',
      format: 'letter',
      orientation: 'portrait'
    });

    const pdfWidth = 8.5;
    const pdfHeight = 11;
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    // Add image to first page
    pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);

    // Handle multi-page content if needed
    let remainingHeight = imgHeight - pdfHeight;
    let pageNumber = 1;
    
    while (remainingHeight > 0) {
      pdf.addPage();
      const yOffset = -(pdfHeight * pageNumber);
      pdf.addImage(imgData, 'JPEG', 0, yOffset, imgWidth, imgHeight);
      remainingHeight -= pdfHeight;
      pageNumber++;
    }

    // Generate blob
    const blob = pdf.output('blob');
    const fileSize = blob.size;

    console.log('🔵 [PDF] PDF generated successfully. Size:', fileSize, 'bytes');

    // Upload to Supabase
    const path = generatedFilePath(null, documentName || "document", documentId);

    const { error: uploadError } = await supabase.storage
      .from("documents")
      .upload(path, blob, {
        contentType: "application/pdf",
        upsert: true,
      });

    if (uploadError) throw uploadError;

    // Buckets are private — store path as reference; signed URLs generated on access
    const file_url = path;

    // Store signature field metadata
    if (signatureFields.length > 0) {
      const { error: metadataError } = await supabase
        .from('generated_documents')
        .update({ signature_fields_metadata: signatureFields })
        .eq('id', documentId);
        
      if (metadataError) {
        console.error('Failed to store signature fields metadata:', metadataError);
      } else {
        console.log('✅ [SIGNATURE FIELDS] Metadata stored in database');
      }
    }

    return { file_url, file_path: path, file_size: fileSize };

  } finally {
    // Clean up - remove iframe
    document.body.removeChild(iframe);
  }
}

// Generate a PDF preview without uploading to storage
export async function generatePdfPreview(html: string): Promise<string> {
  console.log('🔵 [PDF Preview] Opening native print dialog');

  // Create print window
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (!printWindow) {
    throw new Error('Pop-up blocked. Please allow pop-ups to preview PDF.');
  }

  // Write HTML with print-optimized styles and font override
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Document Preview</title>
      <style>
        /* Ecclesiastical Document Styling for Print Preview */
        * {
          font-family: Cambria, Georgia, 'Times New Roman', serif !important;
        }
        
        @page {
          size: Letter;
          margin: 0.75in;
        }
        
        @media print {
          body {
            margin: 0;
            padding: 0;
            font-family: Cambria, Georgia, 'Times New Roman', serif !important;
            font-size: 11pt;
            line-height: 1.85;
            color: #1a1a1a;
          }
        }
        
        @media screen {
          body {
            max-width: 8.5in;
            margin: 0 auto;
            padding: 0.75in;
            font-family: Cambria, Georgia, 'Times New Roman', serif !important;
            font-size: 11pt;
            line-height: 1.85;
            background: #fffef9;
          }
        }
        
        h1 { 
          font-size: 18pt; 
          font-weight: bold; 
          margin: 0 0 20pt 0;
          text-align: center;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          border-bottom: 2px solid #c9a961;
          padding-bottom: 14pt;
        }
        
        h2 { 
          font-size: 13pt; 
          font-weight: bold; 
          margin: 24pt 0 10pt 0;
          border-bottom: 1px solid #bfbfbf;
          padding-bottom: 6pt;
          color: #262626;
        }
        
        h3 { 
          font-size: 12pt; 
          font-weight: bold; 
          font-style: italic;
          margin: 18pt 0 8pt 0;
          color: #333;
        }
        
        p { 
          margin: 0 0 14pt 0;
          font-size: 11pt;
          text-align: justify;
        }
        
        /* Intro paragraphs - italicized with gold accent */
        .intro, p.intro, p:first-of-type {
          font-style: italic;
          color: #4d4d4d;
          border-left: 3px solid #c9a961;
          padding-left: 16pt;
          margin-bottom: 20pt;
        }
        
        /* Scripture references */
        .reference, em {
          font-style: italic;
          color: #666;
        }
        
        /* Conclusion statements */
        .conclusion {
          text-align: center;
          font-weight: bold;
          font-size: 12pt;
          margin-top: 28pt;
        }
        
        li { 
          margin: 8pt 0;
          font-size: 11pt;
          line-height: 1.7;
        }
        
        ul, ol {
          margin: 14pt 0;
          padding-left: 28pt;
        }
        
        strong, b { font-weight: bold; }
        
        hr {
          border: none;
          border-top: 1px solid #ccc;
          margin: 24pt 0;
        }
        
        /* Signature block styles */
        .signature-block {
          margin-top: 40pt;
          page-break-inside: avoid;
        }
        
        .signature-section {
          margin-top: 40pt;
          padding-top: 24pt;
          border-top: 1px solid #ccc;
        }
        
        .signature-line {
          border-bottom: 1px solid #000;
          width: 280pt;
          margin-bottom: 6pt;
        }
      </style>
    </head>
    <body>
      ${html}
      <script>
        window.onload = function() {
          setTimeout(() => window.print(), 500);
        };
      </script>
    </body>
    </html>
  `);
  
  printWindow.document.close();
  return 'print-preview';
}
